/**
 * Notification Dropdown Component
 *
 * Dropdown panel displaying grouped notifications.
 * Features:
 * - Header with "Mark all as read" action
 * - Scrollable list of notification items
 * - Empty state when no notifications
 * - Loading state support
 */

import { useCallback, useEffect, useRef } from 'react';
import { useNotifications } from '../../contexts/NotificationContext';
import { NotificationItem } from './NotificationItem';
import './NotificationDropdown.css';

interface NotificationDropdownProps {
  isOpen: boolean;
  onClose: () => void;
}

export function NotificationDropdown({ isOpen, onClose }: NotificationDropdownProps) {
  const {
    groupedNotifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    refresh,
  } = useNotifications();

  const dropdownRef = useRef<HTMLDivElement>(null);

  // Handle click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        // Check if click was on the bell button (parent handles that)
        const target = event.target as HTMLElement;
        if (!target.closest('.notification-bell')) {
          onClose();
        }
      }
    };

    // Handle escape key
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Refresh when opened
  useEffect(() => {
    if (isOpen) {
      refresh();
    }
  }, [isOpen, refresh]);

  const handleMarkAllAsRead = useCallback(() => {
    markAllAsRead();
  }, [markAllAsRead]);

  if (!isOpen) return null;

  // Flatten groups for display - show primary notification from each group
  const notificationsToShow = groupedNotifications.flatMap((group) => {
    if (group.count === 1) {
      return group.notifications;
    }
    // For grouped notifications, show the primary with count indicator
    return [
      {
        ...group.primaryNotification,
        // Override message to show group summary
        message: group.summary,
      },
    ];
  });

  return (
    <div
      ref={dropdownRef}
      className="notification-dropdown"
      role="menu"
      aria-label="Notifications"
    >
      {/* Header */}
      <div className="notification-dropdown__header">
        <h3 className="notification-dropdown__title">Notifications</h3>
        {unreadCount > 0 && (
          <button
            className="notification-dropdown__mark-read"
            onClick={handleMarkAllAsRead}
            type="button"
          >
            Mark all as read
          </button>
        )}
      </div>

      {/* Content */}
      <div className="notification-dropdown__content">
        {isLoading && notificationsToShow.length === 0 ? (
          <div className="notification-dropdown__loading">
            <div className="notification-dropdown__spinner" />
            <span>Loading notifications...</span>
          </div>
        ) : notificationsToShow.length === 0 ? (
          <div className="notification-dropdown__empty">
            <svg
              className="notification-dropdown__empty-icon"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
            </svg>
            <span className="notification-dropdown__empty-title">You're all caught up!</span>
            <span className="notification-dropdown__empty-text">
              No new notifications at the moment.
            </span>
          </div>
        ) : (
          <div className="notification-dropdown__list">
            {notificationsToShow.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onMarkAsRead={markAsRead}
                onClose={onClose}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer - only show if there are notifications */}
      {notificationsToShow.length > 0 && (
        <div className="notification-dropdown__footer">
          <span className="notification-dropdown__footer-text">
            {unreadCount > 0
              ? `${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}`
              : 'All notifications read'}
          </span>
        </div>
      )}
    </div>
  );
}
